import { useEffect, useState } from 'react';
import { Link2, UserPlus, Ban, Unlink, Search, Loader2, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { clientsAPI } from '../../lib/clientsAPI';
import { emailAPI, errMsg, notifyCommsChanged } from '../../lib/communicationsAPI';

const CREATE_ROLES = ['owner', 'admin', 'accountant', 'bookkeeper', 'staff'];

const NewClientDialog = ({ email, onClose, onCreated }) => {
  const [f, setF] = useState({ display_name: '', entity_type: 'individual' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const r = await clientsAPI.create(f); onCreated(r.data.id); } catch (e) { toast.error(errMsg(e, 'Could not create client')); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="unmatched-new-client-dialog">
        <DialogHeader><DialogTitle>Create client for {email}</DialogTitle></DialogHeader>
        <Input value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} placeholder="Client name" data-testid="unmatched-new-client-name" />
        <Select value={f.entity_type} onValueChange={(v) => setF({ ...f, entity_type: v })}>
          <SelectTrigger data-testid="unmatched-new-client-type"><SelectValue /></SelectTrigger>
          <SelectContent>{['individual', 'company', 'trust', 'smsf', 'sole_trader'].map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !f.display_name.trim()} data-testid="unmatched-new-client-save">{busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Create &amp; link</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Unmatched / needs-confirmation email → link to a client (optionally remember the address), or mark not client-related.
export const UnmatchedBar = ({ data, onChanged }) => {
  const c = data.conversation;
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [remember, setRemember] = useState(true);
  const [role, setRole] = useState(null);
  const [creating, setCreating] = useState(false);
  const pending = ['unmatched', 'needs_confirmation'].includes(c.match_status);
  useEffect(() => { api.get('/api/me').then((r) => setRole(r.data.active_membership?.role)).catch(() => {}); }, []);
  useEffect(() => {
    if (!pending) return undefined;
    const t = setTimeout(() => emailAPI.clientSearch(q).then((r) => setResults(r.data.clients)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, pending]);
  if (c.channel !== 'email') return null;
  const done = (msg) => { toast.success(msg); notifyCommsChanged(); onChanged(); };
  const link = (id) => emailAPI.link(c.id, id, remember).then((r) => done(r.data.aliases_created.length ? `Linked · will auto-link ${r.data.aliases_created.join(', ')}` : 'Linked to client')).catch((e) => toast.error(errMsg(e)));

  if (!pending) {
    return (
      <div className="flex items-center gap-2 px-4 py-1.5 text-xs border-b border-border bg-muted/30" data-testid="email-link-status">
        {c.match_status === 'not_client' ? <><Ban className="w-3.5 h-3.5" />Marked not client-related</> : <><Link2 className="w-3.5 h-3.5 text-primary" />Linked to {data.client?.display_name || 'client'}</>}
        <button type="button" className="ml-auto text-primary hover:underline flex items-center gap-1" onClick={() => emailAPI.unlink(c.id).then(() => done('Moved back to Unmatched')).catch((e) => toast.error(errMsg(e)))} data-testid="email-unlink-btn"><Unlink className="w-3 h-3" />Unlink</button>
      </div>
    );
  }
  return (
    <div className="px-4 py-3 border-b border-border bg-amber-50/70 dark:bg-amber-950/20 space-y-2" data-testid="unmatched-bar">
      <p className="text-sm font-medium flex items-center gap-2"><HelpCircle className="w-4 h-4 text-amber-600" />
        {c.match_status === 'needs_confirmation' ? `${c.counterpart_email} belongs to several clients — pick one` : `No client matches ${c.counterpart_email || 'this sender'}`}</p>
      {data.match_candidates?.length > 0 && (
        <div className="flex flex-wrap gap-2">{data.match_candidates.map((m) => (
          <Button key={m.id} size="sm" variant="outline" className="h-7" onClick={() => link(m.id)} data-testid={`unmatched-candidate-${m.id}`}><Link2 className="w-3.5 h-3.5 mr-1" />{m.display_name}</Button>))}</div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-64"><Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="h-8 pl-7 text-sm" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients to link" data-testid="unmatched-client-search" /></div>
        <label className="flex items-center gap-1.5 text-xs"><Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="unmatched-remember" />Remember this email address for this client</label>
        <Button size="sm" variant="ghost" className="h-8 ml-auto" onClick={() => emailAPI.notClient(c.id).then(() => done('Hidden from Inbox (still searchable)')).catch((e) => toast.error(errMsg(e)))} data-testid="unmatched-not-client"><Ban className="w-3.5 h-3.5 mr-1" />Not client-related</Button>
        {CREATE_ROLES.includes(role) && <Button size="sm" variant="outline" className="h-8" onClick={() => setCreating(true)} data-testid="unmatched-create-client"><UserPlus className="w-3.5 h-3.5 mr-1" />Create new client</Button>}
      </div>
      {results.length > 0 && (
        <div className="max-h-40 overflow-y-auto rounded border border-border bg-background divide-y" data-testid="unmatched-search-results">{results.map((r) => (
          <button key={r.id} type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center gap-2" onClick={() => link(r.id)} data-testid={`unmatched-link-${r.id}`}>
            {r.display_name}<span className="text-[11px] text-muted-foreground capitalize ml-auto">{(r.entity_type || '').replace('_', ' ')}</span></button>))}</div>
      )}
      {creating && <NewClientDialog email={c.counterpart_email} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); link(id); }} />}
    </div>
  );
};
